import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import api from '../services/api';
import { parseApiHeaders } from '../lib/parseApiHeaders';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Globe,
  Settings,
  Zap,
  Clock,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const ApiConfigs = () => {
  const { user } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    api_owner: '',
    api_function: '',
    endpoint: '',
    method: 'POST',
    headers: '{}',
    timeout: 120000,
    is_active: true,
    description: ''
  });
  const [errors, setErrors] = useState({});

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      return;
    }
  }, [user]);

  // Carregar configurações
  useEffect(() => {
    if (user?.role === 'admin') {
      loadConfigs();
    }
  }, [user]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api-configs');
      
      if (response.data.success || response.data.configs) {
        const configsList = response.data.configs || [];
        setConfigs(configsList);
      } else {
        toast.error('Erro ao carregar configurações de API');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações de API');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async (e) => {
    e.preventDefault();
    
    // Validação
    const newErrors = {};
    if (!formData.api_owner.trim()) newErrors.api_owner = 'API Owner é obrigatório';
    if (!formData.api_function.trim()) newErrors.api_function = 'API Function é obrigatória';
    if (!formData.endpoint.trim()) newErrors.endpoint = 'Endpoint é obrigatório';
    if (!formData.method) newErrors.method = 'Método HTTP é obrigatório';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let parsedHeaders = {};
      try {
        parsedHeaders = parseApiHeaders(formData.headers);
      } catch (error) {
        setErrors({ headers: 'Informe os headers como objeto. Exemplo: { Token_Autorizacao: valor, Content-Type: application/json }' });
        return;
      }

      const configData = {
        ...formData,
        headers: parsedHeaders,
        timeout: parseInt(formData.timeout)
      };

      const response = await api.post('/api-configs', configData);

      if (response.data.success || response.data.message) {
        toast.success('Configuração de API criada com sucesso!');
        setShowCreateModal(false);
        resetForm();
        loadConfigs();
      } else {
        toast.error(response.data.error || 'Erro ao criar configuração');
      }
    } catch (error) {
      console.error('Erro ao criar configuração:', error);
      toast.error('Erro ao criar configuração de API');
    }
  };

  const handleEditConfig = async (e) => {
    e.preventDefault();
    
    // Validação
    const newErrors = {};
    if (!formData.api_owner.trim()) newErrors.api_owner = 'API Owner é obrigatório';
    if (!formData.api_function.trim()) newErrors.api_function = 'API Function é obrigatória';
    if (!formData.endpoint.trim()) newErrors.endpoint = 'Endpoint é obrigatório';
    if (!formData.method) newErrors.method = 'Método HTTP é obrigatório';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let parsedHeaders = {};
      try {
        parsedHeaders = parseApiHeaders(formData.headers);
      } catch (error) {
        setErrors({ headers: 'Informe os headers como objeto. Exemplo: { Token_Autorizacao: valor, Content-Type: application/json }' });
        return;
      }

      const configData = {
        ...formData,
        headers: parsedHeaders,
        timeout: parseInt(formData.timeout)
      };

      const response = await api.put(`/api-configs/${editingConfig.id}`, configData);

      if (response.data.success || response.data.message) {
        toast.success('Configuração de API atualizada com sucesso!');
        setShowEditModal(false);
        setEditingConfig(null);
        resetForm();
        loadConfigs();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar configuração');
      }
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      toast.error('Erro ao atualizar configuração de API');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta configuração de API?')) {
      return;
    }

    try {
      const response = await api.delete(`/api-configs/${configId}`);

      if (response.data.success || response.data.message) {
        toast.success('Configuração de API excluída com sucesso!');
        loadConfigs();
      } else {
        toast.error(response.data.error || 'Erro ao excluir configuração');
      }
    } catch (error) {
      console.error('Erro ao excluir configuração:', error);
      toast.error('Erro ao excluir configuração de API');
    }
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      api_owner: config.api_owner,
      api_function: config.api_function,
      endpoint: config.endpoint,
      method: config.method,
      headers: JSON.stringify(config.headers, null, 2),
      timeout: config.timeout,
      is_active: config.is_active,
      description: config.description || ''
    });
    setShowEditModal(true);
    setErrors({});
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setEditingConfig(null);
    resetForm();
    setErrors({});
  };

  const resetForm = () => {
    setFormData({
      api_owner: '',
      api_function: '',
      endpoint: '',
      method: 'POST',
      headers: '{}',
      timeout: 120000,
      is_active: true,
      description: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const filteredConfigs = configs.filter(config => {
    const matchesSearch = 
      config.api_owner.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.api_function.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.endpoint.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOwner = !filterOwner || config.api_owner === filterOwner;
    const matchesStatus = !filterStatus || config.is_active === (filterStatus === 'active');
    return matchesSearch && matchesOwner && matchesStatus;
  });

  const uniqueOwners = [...new Set(configs.map(config => config.api_owner))];

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
          <p className="text-gray-600">Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Configurações de API</h1>
          <p className="text-gray-600">Gerencie as configurações de integração com APIs externas</p>
        </div>
        
        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar configurações..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filterOwner}
                onChange={(e) => setFilterOwner(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os proprietários</option>
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
              </select>
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Nova Configuração
              </button>
            </div>
          </div>
        </div>

        {/* Lista de Configurações */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Configuração
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Endpoint
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timeout
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConfigs.map((config) => (
                  <tr key={config.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <Globe className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{config.api_owner}</div>
                          <div className="text-sm text-gray-500">{config.api_function}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={config.endpoint}>
                        {config.endpoint}
                      </div>
                      {config.description && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={config.description}>
                          {config.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        config.method === 'GET' ? 'bg-green-100 text-green-800' :
                        config.method === 'POST' ? 'bg-primary-100 text-primary-800' :
                        config.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                        config.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {config.method}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-1" />
                        {config.timeout / 1000}s
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {config.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ativa
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inativa
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(config)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteConfig(config.id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal de Criação */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Nova Configuração de API</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateConfig} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Owner *
                    </label>
                    <input
                      type="text"
                      name="api_owner"
                      value={formData.api_owner}
                      onChange={handleInputChange}
                      placeholder="Ex: Maxpar, Seguradora X"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.api_owner ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.api_owner && <p className="text-red-500 text-sm mt-1">{errors.api_owner}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Function *
                    </label>
                    <input
                      type="text"
                      name="api_function"
                      value={formData.api_function}
                      onChange={handleInputChange}
                      placeholder="Ex: Inserção de apólices"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.api_function ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.api_function && <p className="text-red-500 text-sm mt-1">{errors.api_function}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endpoint *
                  </label>
                  <input
                    type="url"
                    name="endpoint"
                    value={formData.endpoint}
                    onChange={handleInputChange}
                    placeholder="https://api.exemplo.com/v1/endpoint"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.endpoint ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.endpoint && <p className="text-red-500 text-sm mt-1">{errors.endpoint}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Método HTTP *
                    </label>
                    <select
                      name="method"
                      value={formData.method}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.method ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                    {errors.method && <p className="text-red-500 text-sm mt-1">{errors.method}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout (ms)
                    </label>
                    <input
                      type="number"
                      name="timeout"
                      value={formData.timeout}
                      onChange={handleInputChange}
                      min="1000"
                      max="300000"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Ativa</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Headers
                  </label>
                  <textarea
                    name="headers"
                    value={formData.headers}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder={'{\n  Token_Autorizacao: seu-token,\n  Content-Type: application/json\n}'}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm ${
                      errors.headers ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.headers && <p className="text-red-500 text-sm mt-1">{errors.headers}</p>}
                  <p className="text-xs text-gray-500 mt-1">Chaves e valores podem ir sem aspas.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Descrição adicional da integração..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Criar Configuração
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Edição */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Editar Configuração de API</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleEditConfig} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Owner *
                    </label>
                    <input
                      type="text"
                      name="api_owner"
                      value={formData.api_owner}
                      onChange={handleInputChange}
                      placeholder="Ex: Maxpar, Seguradora X"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.api_owner ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.api_owner && <p className="text-red-500 text-sm mt-1">{errors.api_owner}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Function *
                    </label>
                    <input
                      type="text"
                      name="api_function"
                      value={formData.api_function}
                      onChange={handleInputChange}
                      placeholder="Ex: Inserção de apólices"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.api_function ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.api_function && <p className="text-red-500 text-sm mt-1">{errors.api_function}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endpoint *
                  </label>
                  <input
                    type="url"
                    name="endpoint"
                    value={formData.endpoint}
                    onChange={handleInputChange}
                    placeholder="https://api.exemplo.com/v1/endpoint"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.endpoint ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.endpoint && <p className="text-red-500 text-sm mt-1">{errors.endpoint}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Método HTTP *
                    </label>
                    <select
                      name="method"
                      value={formData.method}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.method ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                    {errors.method && <p className="text-red-500 text-sm mt-1">{errors.method}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout (ms)
                    </label>
                    <input
                      type="number"
                      name="timeout"
                      value={formData.timeout}
                      onChange={handleInputChange}
                      min="1000"
                      max="300000"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Ativa</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Headers
                  </label>
                  <textarea
                    name="headers"
                    value={formData.headers}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder={'{\n  Token_Autorizacao: seu-token,\n  Content-Type: application/json\n}'}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm ${
                      errors.headers ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.headers && <p className="text-red-500 text-sm mt-1">{errors.headers}</p>}
                  <p className="text-xs text-gray-500 mt-1">Chaves e valores podem ir sem aspas.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Descrição adicional da integração..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApiConfigs;
